import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, Plus, Upload, ChevronDown, FolderOpen } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FilterDropdown, type FilterOption } from '@/components/ui/FilterDropdown'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SourceIcon } from '@/components/ui/source-icon'
import { useDocumentsStore } from '@/stores/documents-store'
import { useSharePointGate } from '@/hooks/useSharePointGate'
import { SharePointBrowser } from '@/components/SharePointBrowser'
import type { DocumentCategory } from '@/lib/schemas'
import {
  bidDocumentCategory,
  bidDocumentCategoryLabel,
  compareBidDocumentCategories,
} from '@/lib/bid-document-groups'

interface BidDocumentsToolbarProps {
  bidId: string
  documentCount: number
  /** Opens the native file picker (provided by useBidDocumentUpload). */
  onOpenFilePicker: () => void
  /** Opens the native folder picker (provided by useBidDocumentUpload). */
  onOpenFolderPicker?: (() => void) | undefined
  readOnly?: boolean
}

/**
 * Toolbar for the Bid Details → Documents tab.
 *  - Search (filename, debounced via store)
 *  - Category filter (multi-select)
 *  - Add menu: Upload files / Upload folder / Import from SharePoint (or Connect SharePoint)
 *
 * Filter + search state lives on the documents-store so DocumentList can
 * read it via getVisibleDocuments().
 */
export function BidDocumentsToolbar({
  bidId,
  documentCount,
  onOpenFilePicker,
  onOpenFolderPicker,
  readOnly = false,
}: BidDocumentsToolbarProps) {
  const searchQuery = useDocumentsStore((s) => s.searchQuery)
  const setSearchQuery = useDocumentsStore((s) => s.setSearchQuery)
  const categoryFilter = useDocumentsStore((s) => s.categoryFilter)
  const toggleCategoryFilter = useDocumentsStore((s) => s.toggleCategoryFilter)
  const clearCategoryFilter = useDocumentsStore((s) => s.clearCategoryFilter)
  const documents = useDocumentsStore((s) => s.documents)

  const { isConnected, ensureChecked, openOrConnect } = useSharePointGate()
  const fetchDocuments = useDocumentsStore((s) => s.fetchDocuments)

  const [browserOpen, setBrowserOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Probe SharePoint connection once on mount — cheap, cached.
    ensureChecked()
  }, [ensureChecked])

  // Category options only show the groups the bid's documents currently sit
  // under (the same category the list groups by), in the list's group order —
  // keeps the filter useful and avoids empty results.
  const categoryOptions: FilterOption[] = useMemo(() => {
    const present = new Set<DocumentCategory>()
    for (const doc of documents) present.add(bidDocumentCategory(doc))
    return Array.from(present)
      .sort(compareBidDocumentCategories)
      .map((c) => ({ value: c, label: bidDocumentCategoryLabel(c) }))
  }, [documents])

  const handleClickImportFromSharePoint = () => {
    openOrConnect(() => setBrowserOpen(true))
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-muted-foreground mr-2">
          Tender/RFP Documents{documentCount > 0 && ` (${documentCount})`}
        </h2>

        {/* Search */}
        <div className="relative max-w-xs flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-8"
            data-testid="bid-documents-search"
          />
        </div>

        {/* Category filter — only render when there's more than one category in use */}
        {categoryOptions.length > 1 && (
          <FilterDropdown
            label="Category"
            options={categoryOptions}
            selected={categoryFilter as Set<string>}
            onToggle={(v) => toggleCategoryFilter(v as DocumentCategory)}
            onClear={clearCategoryFilter}
            className="h-8"
          />
        )}

        <div className="flex-1" />

        {!readOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-8" data-testid="bid-documents-add">
                <Plus className="h-4 w-4 mr-1.5" />
                Add
                <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onClick={onOpenFilePicker}>
                <Upload className="h-4 w-4 mr-2" />
                Upload files
              </DropdownMenuItem>
              {onOpenFolderPicker && (
                <DropdownMenuItem onClick={onOpenFolderPicker}>
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Upload folder
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleClickImportFromSharePoint}>
                <SourceIcon source="sharepoint" size={16} className="mr-2" />
                {isConnected ? 'Import from SharePoint' : 'Connect SharePoint'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* SharePoint dialog — lazy mounted */}
      {browserOpen && (
        <SharePointBrowser
          open={browserOpen}
          onClose={() => setBrowserOpen(false)}
          mode="pick"
          defaultBidId={bidId}
          onSuccess={() => {
            void fetchDocuments(bidId)
          }}
        />
      )}
    </>
  )
}

